// Estado de uma lista com filtros (issue #93): dono dos filtros, da busca, da
// página e da ordenação. As treze telas de lista repetiam à mão o mesmo
// comportamento — voltar à página 1 a cada filtro (50 vezes), zerar filtro
// por filtro no "Limpar Filtros" (filtro esquecido = filtro que não limpa),
// decidir a mensagem de vazio. Aqui é uma regra só. Puro (sem DOM): os
// componentes de filtro só refletem e alimentam este estado.

import type { EstadoTabela } from "../ui.ts";

const copia = <T>(v: T): T => (Array.isArray(v) ? ([...v] as T) : v);

export class EstadoLista<F extends Record<string, unknown>> {
  private readonly padroes: F;
  private valores: F;
  private textoBusca = "";
  /** Página, tamanho e ordenação da tabela (`tabelaPaginada`). */
  readonly tabela: EstadoTabela;

  /**
   * `padroes`: cada filtro e o valor dele ao abrir e depois de "Limpar
   * Filtros". Em filtro de seleção múltipla, `null` = todos marcados.
   */
  constructor(padroes: F, tabela: Partial<EstadoTabela> = {}) {
    this.padroes = Object.fromEntries(Object.entries(padroes).map(([k, v]) => [k, copia(v)])) as F;
    this.valores = this.copiaDosPadroes();
    this.tabela = { page: 1, size: 20, ordCol: null, ordDir: "asc", ...tabela };
  }

  private copiaDosPadroes(): F {
    return Object.fromEntries(Object.entries(this.padroes).map(([k, v]) => [k, copia(v)])) as F;
  }

  filtro<K extends keyof F>(chave: K): F[K] {
    return this.valores[chave];
  }

  /** Muda um filtro — e volta à página 1, sempre. */
  mudarFiltro<K extends keyof F>(chave: K, valor: F[K]): void {
    this.valores[chave] = copia(valor);
    this.tabela.page = 1;
  }

  get busca(): string {
    return this.textoBusca;
  }

  /** Muda a busca — e volta à página 1. */
  buscar(texto: string): void {
    this.textoBusca = texto;
    this.tabela.page = 1;
  }

  /** "Limpar Filtros": todos os filtros e a busca ao padrão; ordenação e tamanho ficam. */
  limpar(): void {
    this.valores = this.copiaDosPadroes();
    this.textoBusca = "";
    this.tabela.page = 1;
  }

  /** Algum filtro fora do padrão, ou busca preenchida. */
  filtrando(): boolean {
    if (this.textoBusca.trim() !== "") return true;
    return Object.keys(this.padroes).some((k) => JSON.stringify(this.valores[k]) !== JSON.stringify(this.padroes[k]));
  }

  /** A mensagem da lista vazia: por filtro ou porque não há nada. */
  mensagemVazio(m: { nenhum: string; filtrado: string }): string {
    return this.filtrando() ? m.filtrado : m.nenhum;
  }
}
